<template>
  <div class="documents-panel">
    <!-- Checklist de obligatorios -->
    <div v-if="checklist.length" class="checklist mb-3">
      <div class="checklist-title">Documentación requerida</div>
      <div v-for="item in checklist" :key="item.document_type_id" class="checklist-item">
        <i :class="checklistIcon(item).icon" :style="{ color: checklistIcon(item).color }"></i>
        <span class="checklist-name">{{ item.document_type_name }}</span>
        <span class="checklist-detail">{{ checklistDetail(item) }}</span>
        <span v-if="!item.is_required" class="badge badge-gray ml-2">Opcional</span>
      </div>
    </div>

    <div class="table-container">
      <div class="table-header d-flex justify-content-between align-items-center">
        <h3 class="m-0">Documentos</h3>
        <b-button variant="none" class="button button-primary" @click="openUpload">
          + Subir documento
        </b-button>
      </div>

      <div class="table-responsive mb-0">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th>Tipo</th>
              <th class="text-center">Número</th>
              <th class="text-center">Emitido</th>
              <th class="text-center">Vence</th>
              <th class="text-center">Estado</th>
              <th class="text-center">Archivo</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="doc in documents" :key="doc.id">
              <td>{{ doc.document_type_name }}</td>
              <td class="text-center">{{ doc.document_number || "-" }}</td>
              <td class="text-center">{{ doc.issue_date || "-" }}</td>
              <td class="text-center" :class="expirationClass(doc)">
                {{ doc.expiration_date || "-" }}
                <div v-if="doc.days_to_expire != null" class="small">
                  {{ expirationHint(doc) }}
                </div>
              </td>
              <td class="text-center">
                <span :class="`badge ${statusMeta(doc.doc_status).color}`">
                  {{ statusMeta(doc.doc_status).label }}
                </span>
              </td>
              <td class="text-center">
                <button type="button" class="link-button" @click="openFile(doc)">
                  <i class="ri-eye-line"></i> Ver
                </button>
              </td>
              <td class="text-center">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem text="Editar" @click="openEdit(doc)" />
                    <DropdownItem text="Eliminar" @click="confirmDelete(doc)" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!documents.length" class="table-no-results">
          <p class="m-0 px-3">Sin documentos subidos.</p>
        </div>
      </div>
    </div>

    <!-- Modal subir / editar -->
    <b-modal
      id="document-modal"
      :title="editing ? 'Editar documento' : 'Subir documento'"
      hide-footer
      size="md"
      @hidden="resetForm"
    >
      <form class="custom-form" @submit.prevent="submit" novalidate>
        <div class="form-group" v-if="!editing">
          <label class="label">Tipo de documento *</label>
          <FormSelect
            v-model="form.document_type_id"
            :items="typeOptions"
            :showError="$v.form.document_type_id.$error"
          />
          <FormError :show="$v.form.document_type_id.$error" message="Selecciona un tipo" />
        </div>
        <div class="form-group" v-else>
          <label class="label">Tipo de documento</label>
          <input type="text" class="custom-form-control" :value="form.document_type_name" disabled />
        </div>

        <div class="form-group" v-if="!editing">
          <label class="label">Archivo * <span class="text-muted small">(PDF, JPG o PNG, máx 10 MB)</span></label>
          <input
            type="file"
            class="custom-form-control"
            accept=".pdf,.jpg,.jpeg,.png"
            @change="onFileChange"
          />
          <FormError :show="fileError !== ''" :message="fileError" />
        </div>

        <div class="form-group">
          <label class="label">Número</label>
          <input type="text" class="custom-form-control" v-model="form.document_number" maxlength="50" />
        </div>

        <div class="row">
          <div class="col-md-6 form-group">
            <label class="label">Fecha de emisión</label>
            <input type="date" class="custom-form-control" v-model="form.issue_date" />
          </div>
          <div class="col-md-6 form-group">
            <label class="label">Fecha de vencimiento{{ requiresExpiration ? " *" : "" }}</label>
            <input type="date" class="custom-form-control" v-model="form.expiration_date" />
            <FormError
              :show="$v.form.expiration_date.$error"
              message="Este tipo de documento requiere fecha de vencimiento"
            />
          </div>
        </div>

        <div class="form-group" v-if="editing">
          <label class="label">Estado</label>
          <FormSelect v-model="form.doc_status" :items="statusOptions" />
        </div>

        <div class="form-group">
          <label class="label">Notas</label>
          <textarea class="custom-form-control" rows="2" v-model="form.notes" maxlength="500"></textarea>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="none" class="button button-light mr-2" @click="$bvModal.hide('document-modal')">
            Cancelar
          </b-button>
          <b-button variant="none" class="button button-primary" type="submit" :disabled="saving">
            {{ saving ? "Guardando..." : "Guardar" }}
          </b-button>
        </div>
      </form>
    </b-modal>

    <ConfirmationModal
      :show="confirmationModal.show"
      @hide="closeConfirmationModal"
      :data="confirmationModal"
      @confirm="confirmModal"
    />
  </div>
</template>

<script>
import {
  Alert,
  Util,
  DropdownTable,
  DropdownItem,
  ConfirmationModal,
  ConfirmationModalMixin,
  FormSelect,
  FormError,
} from "wize-admin";
import { required } from "vuelidate/lib/validators";

import { DocumentService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

// Panel de documentos reutilizable (DEF-ADM-019 §4.2). Se monta en el detalle de
// un proveedor, un vehículo o un conductor — propios y de terceros por igual.
export default {
  name: "DocumentsPanel",
  components: { DropdownTable, DropdownItem, ConfirmationModal, FormSelect, FormError },
  mixins: [ConfirmationModalMixin],
  props: {
    entityType: { type: String, required: true }, // VEHICLE | DRIVER | PROVIDER
    entityId: { type: [String, Number], required: true },
  },
  data() {
    return {
      documents: [],
      checklist: [],
      types: [],
      editing: null,
      file: null,
      fileError: "",
      saving: false,
      toDelete: null,
      form: {
        document_type_id: null,
        document_type_name: "",
        document_number: "",
        issue_date: "",
        expiration_date: "",
        doc_status: "",
        notes: "",
      },
    };
  },
  validations() {
    return {
      form: {
        document_type_id: this.editing ? {} : { required },
        expiration_date: this.requiresExpiration ? { required } : {},
      },
    };
  },
  computed: {
    typeOptions() {
      return this.types.map((t) => ({ key: t.id, label: t.name }));
    },
    statusOptions() {
      return Object.values(LocalConstants.DocumentStatus).map((s) => ({
        key: s.key,
        label: s.label,
      }));
    },
    // El tipo seleccionado obliga a poner fecha de vencimiento.
    requiresExpiration() {
      const id = this.editing ? this.editing.document_type_id : this.form.document_type_id;
      const type = this.types.find((t) => t.id === Number(id));
      return type ? type.has_expiration : false;
    },
  },
  watch: {
    entityId() {
      this.load();
    },
  },
  methods: {
    statusMeta(key) {
      return Util.searchValue(key, LocalConstants.DocumentStatus) || { label: key, color: "badge-gray" };
    },
    // Rojo si venció, naranja si le quedan 30 días o menos (CA-08).
    expirationClass(doc) {
      if (doc.days_to_expire == null) return "";
      if (doc.days_to_expire <= 0) return "text-expired";
      if (doc.days_to_expire <= 30) return "text-expiring";
      return "";
    },
    expirationHint(doc) {
      if (doc.days_to_expire <= 0) return `Venció hace ${Math.abs(doc.days_to_expire)} día(s)`;
      if (doc.days_to_expire <= 30) return `Vence en ${doc.days_to_expire} día(s)`;
      return "";
    },
    checklistIcon(item) {
      if (!item.uploaded) {
        return item.is_required
          ? { icon: "ri-close-circle-line", color: "#dc3545" }
          : { icon: "ri-checkbox-blank-circle-line", color: "#adb5bd" };
      }
      if (item.doc_status === "EXPIRED" || (item.days_to_expire != null && item.days_to_expire <= 0)) {
        return { icon: "ri-close-circle-line", color: "#dc3545" };
      }
      if (item.days_to_expire != null && item.days_to_expire <= 30) {
        return { icon: "ri-error-warning-line", color: "#fd7e14" };
      }
      return { icon: "ri-checkbox-circle-line", color: "#28a745" };
    },
    checklistDetail(item) {
      if (!item.uploaded) return "No subido";
      if (item.days_to_expire != null && item.days_to_expire <= 0) {
        return `Vencido el ${item.expiration_date}`;
      }
      if (item.days_to_expire != null && item.days_to_expire <= 30) {
        return `Vence en ${item.days_to_expire} día(s)`;
      }
      if (item.expiration_date) return `Vigente hasta ${item.expiration_date}`;
      return "Vigente";
    },
    async load() {
      try {
        const [docsRes, typesRes] = await Promise.all([
          DocumentService.listByEntity(this.entityType, this.entityId),
          DocumentService.listTypes({ applies_to: this.entityType, status: 1, size: 100 }),
        ]);
        this.documents = docsRes.payload.documents || [];
        this.checklist = docsRes.payload.checklist || [];
        this.types = typesRes.payload.items || [];
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudieron cargar los documentos");
      }
    },
    openUpload() {
      this.editing = null;
      this.resetForm();
      this.$bvModal.show("document-modal");
    },
    openEdit(doc) {
      this.editing = doc;
      this.form = {
        document_type_id: doc.document_type_id,
        document_type_name: doc.document_type_name,
        document_number: doc.document_number || "",
        issue_date: doc.issue_date || "",
        expiration_date: doc.expiration_date || "",
        doc_status: doc.doc_status,
        notes: doc.notes || "",
      };
      this.$bvModal.show("document-modal");
    },
    onFileChange(ev) {
      const file = ev.target.files[0];
      this.fileError = "";
      if (!file) {
        this.file = null;
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        this.fileError = "El archivo supera el máximo de 10 MB";
        this.file = null;
        return;
      }
      this.file = file;
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      if (!this.editing && !this.file) {
        this.fileError = "El archivo es obligatorio";
        return;
      }

      this.saving = true;
      try {
        if (this.editing) {
          await DocumentService.update(this.editing.id, {
            document_number: this.form.document_number || null,
            issue_date: this.form.issue_date || null,
            expiration_date: this.form.expiration_date || null,
            doc_status: this.form.doc_status || null,
            notes: this.form.notes || null,
          });
        } else {
          const data = new FormData();
          data.append("file", this.file);
          data.append("document_type_id", this.form.document_type_id);
          data.append("entity_type", this.entityType);
          data.append("entity_id", this.entityId);
          if (this.form.document_number) data.append("document_number", this.form.document_number);
          if (this.form.issue_date) data.append("issue_date", this.form.issue_date);
          if (this.form.expiration_date) data.append("expiration_date", this.form.expiration_date);
          if (this.form.notes) data.append("notes", this.form.notes);
          await DocumentService.upload(data);
        }
        Alert.success("Documento guardado");
        this.$bvModal.hide("document-modal");
        await this.load();
        this.$emit("changed");
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo guardar el documento");
      } finally {
        this.saving = false;
      }
    },
    async openFile(doc) {
      try {
        const res = await DocumentService.fileUrl(doc.id);
        window.open(res.payload.url, "_blank");
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo abrir el archivo");
      }
    },
    confirmDelete(doc) {
      this.toDelete = doc;
      this.openConfirmationModal({
        title: "Eliminar documento",
        description: "¿Seguro que quieres eliminar este documento?",
        acceptText: "Eliminar",
        action: "delete",
      });
    },
    confirmModal() {
      this.closeConfirmationModal();
      if (this.confirmationModal.action === "delete") this.remove();
    },
    async remove() {
      try {
        await DocumentService.delete(this.toDelete.id);
        Alert.success("Documento eliminado");
        await this.load();
        this.$emit("changed");
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo eliminar el documento");
      } finally {
        this.toDelete = null;
      }
    },
    resetForm() {
      this.form = {
        document_type_id: null,
        document_type_name: "",
        document_number: "",
        issue_date: "",
        expiration_date: "",
        doc_status: "",
        notes: "",
      };
      this.file = null;
      this.fileError = "";
      this.editing = null;
      this.$v.$reset();
    },
  },
  async mounted() {
    await this.load();
  },
};
</script>

<style lang="stylus" scoped>
.checklist
  background #f8f9fa
  border 1px solid #e5e7eb
  border-radius 6px
  padding 12px 16px
.checklist-title
  font-weight 600
  margin-bottom 8px
.checklist-item
  display flex
  align-items center
  gap 8px
  padding 3px 0
  i
    font-size 16px
.checklist-name
  font-weight 500
.checklist-detail
  color #6c757d
  font-size 13px
.text-expired
  color #dc3545
  font-weight 600
.text-expiring
  color #fd7e14
  font-weight 600
// Mismo botón-enlace que el drawer de Viajes, para que "Ver" se vea igual.
.link-button
  background none
  border none
  padding 0
  color #007bff
  font-size 0.82rem
  cursor pointer
</style>
